import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { environment } from 'environments/environment';
import { LayoutComponent } from 'app/layout/layout.component';
import {
    wipDashboardRoutes,
    wipDataManagementRoutes,
} from 'app/modules/well-integrity-planning/well-integrity-planning.route';
import { eRegDataManagementRoutes } from './modules/equipment-registration/equipment-registration.routes';
import { commonRoutes } from './modules/common/common.route';
import { datDataManagementRoutes } from './modules/daily-activity-tracker/daily-activity-tracker.route';
import { cdftDataManagementRoutes } from './modules/cdft/cdft.route';
import { whiDataManagementRoutes } from './modules/well-header-information/well-header-information.route';
import { auditTrailDataManagementRoutes } from './modules/automated-audit-trails/automated-audit-trails.route';
import { wProdStatusManagementRoutes } from './modules/well-production-status/well-production-status.routes';
import {
    wimConfigurationRoutes,
    wimDashboardRoutes,
} from './modules/well-integrity-management/well-integrity-management.route';
import { optionTypeConfigurationRoutes } from './modules/option-type/option-type.route';
import { umConfigurationRoutes } from './modules/user-management/user-management.route';
import {
    wbsDashboardRoutes,
    wbsDataManagementRoutes,
    wsdConfigurationRoutes,
} from './modules/well-schematic-diagram/well-schematic-diagram.route';
import {
    aprDashboardRoutes,
    aprDataManagementRoutes,
} from './modules/annulus-pressure-record/annulus-pressure-record.route';
import {
    whDashboardRoutes,
    whDataManagementRoutes,
} from './modules/well-healthiness/well-healthiness.route';
import {
    cmDashboardRoutes,
    cmDataManagementRoutes,
} from './modules/corrective-maintenance/corrective-maintenance.route';
import { wpbDataManagementRoutes } from './modules/work-planning-budget/wpb.route';
import {
    mocDashboardRoutes,
    mocDataManagementRoutes,
} from './modules/management-of-change/moc.route';

// @formatter:off
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboards/panel'
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
    },

    // Redirect signed-in user to the '/home'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'home',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'pricing-table',
                loadChildren: () =>
                    import('app/modules/landing/pricing-table/pricing-table.routes'),
            },
            {
                path: 'confirmation',
                loadChildren: () =>
                    import('app/modules/auth/confirmation/confirmation.routes'),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import('app/modules/auth/forgot-password/forgot-password.routes'),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/landing.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            // Well Integrity Management
            {
                path: 'wim',
                children: [
                    // Overall Overview Dashboard
                    ...wimDashboardRoutes,

                    // PM Dashboard
                    ...wipDashboardRoutes,

                    // CM Dashboard
                    ...cmDashboardRoutes,

                    // Well Healthiness Dashboard
                    ...whDashboardRoutes,

                    // MOC Dashboard
                    ...mocDashboardRoutes,

                    // APR Dashboard
                    ...aprDashboardRoutes,
                ],
            },

            // WBS Diagram
            ...wbsDashboardRoutes,

            // Data Management
            {
                path: 'data-management',
                children: [
                    // Automated Audit Trails
                    ...auditTrailDataManagementRoutes,

                    // Well Header Information
                    ...whiDataManagementRoutes,

                    // Daily Activity Tracker
                    ...datDataManagementRoutes,

                    // Well Integrity Planning
                    ...wipDataManagementRoutes,

                    // CDFT
                    ...cdftDataManagementRoutes,

                    // Well Production Catgory
                    ...wProdStatusManagementRoutes,

                    // SCE Inventory
                    ...eRegDataManagementRoutes,

                    // Annulus Pressure Record
                    ...aprDataManagementRoutes,

                    // Annulus Pressure Record
                    ...whDataManagementRoutes,

                    // Corrective Maintenance
                    ...cmDataManagementRoutes,

                    // Well Planning Budget
                    ...wpbDataManagementRoutes,

                    // Management of change
                    ...mocDataManagementRoutes,

                    // Well Barrier Status
                    ...wbsDataManagementRoutes,
                ],
            },

            // Configurations
            {
                path: 'configurations',
                children: [
                    // Well Integrity Management
                    ...wimConfigurationRoutes,

                    // Option Type (Metadata)
                    ...optionTypeConfigurationRoutes,

                    // Well Schematic Diagram
                    ...wsdConfigurationRoutes,

                    // User Management
                    ...umConfigurationRoutes,
                ],
            },

            ...commonRoutes,
        ],
    },
];
